import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, ArrowRight, Anchor, Wallet, Users, Sailboat, Ship, MapPin,
  Calendar, Compass, Sparkles, Heart, Mail,
} from "lucide-react";
import { api } from "@/lib/api";
import { useI18n, PHONE_COUNTRIES } from "@/i18n";
import { toast } from "sonner";

const TOTAL_STEPS = 9;

const groupOptions = [
  { value: "solo", it: "Viaggio da solo", en: "Solo traveler", suggested: 1 },
  { value: "couple", it: "Coppia", en: "Couple", suggested: 2 },
  { value: "family", it: "Famiglia", en: "Family", suggested: 4 },
  { value: "friends", it: "Gruppo di amici", en: "Group of friends", suggested: 4 },
  { value: "company", it: "Azienda / team building", en: "Company / team", suggested: 4 },
  { value: "other", it: "Altro", en: "Other", suggested: 4 },
];

const accommOptions = [
  { value: "single_cabin", it: "Cabina singola", en: "Single cabin" },
  { value: "multi_cabin", it: "Più cabine", en: "Multiple cabins" },
  { value: "whole_boat", it: "Intera barca", en: "Whole boat" },
  { value: "advise", it: "Consigliami tu", en: "Advise me" },
];

const boatOptions = [
  { value: "sailboat", icon: Sailboat, it: "Barca a vela", en: "Sailboat" },
  { value: "catamaran", icon: Ship, it: "Catamarano", en: "Catamaran" },
  { value: "yacht", icon: Anchor, it: "Yacht", en: "Yacht" },
  { value: "motorboat", icon: Ship, it: "Motoscafo / barca a motore", en: "Motorboat" },
  { value: "gommone", icon: Ship, it: "Gommone", en: "Rib / Tender" },
  { value: "any", icon: Compass, it: "Non lo so, consigliami tu", en: "Not sure, advise me" },
];

const experienceOptions = [
  { value: "relax", it: "Relax totale", en: "Total relax" },
  { value: "calette", it: "Mare e calette", en: "Sea & coves" },
  { value: "party", it: "Festa e divertimento", en: "Party & fun" },
  { value: "luxury", it: "Lusso", en: "Luxury" },
  { value: "romantic", it: "Esperienza romantica", en: "Romantic" },
  { value: "family_kids", it: "Famiglia con bambini", en: "Family with kids" },
  { value: "adventure", it: "Avventura", en: "Adventure" },
  { value: "sport_sail", it: "Sport / vela", en: "Sport / sailing" },
  { value: "teambuilding", it: "Team building aziendale", en: "Corporate team building" },
  { value: "mix", it: "Mix di tutto", en: "Mix of everything" },
  { value: "other", it: "Altro", en: "Other" },
];

const DEFAULT_DESTINATIONS = [
  "Sicilia", "Isole Eolie", "Isole Egadi", "Isola d'Elba", "Isole Pontine",
  "Ischia", "Capri", "Costiera Amalfitana", "Calabria", "Sardegna",
  "Corsica", "Baleari", "Grecia", "Creta", "Croazia",
  "Seychelles", "Caraibi", "Thailandia",
];

const monthsIt = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const durations = [
  { value: "weekend", it: "Weekend", en: "Weekend" },
  { value: "1week", it: "1 settimana", en: "1 week" },
  { value: "10days", it: "10 giorni", en: "10 days" },
  { value: "2weeks", it: "2 settimane", en: "2 weeks" },
];

function captureSource(searchParams) {
  const fromParam = (k) => searchParams.get(k) || undefined;
  const ref = document.referrer || undefined;
  let channel = fromParam("utm_source");
  if (!channel && ref) {
    const r = ref.toLowerCase();
    if (r.includes("instagram")) channel = "instagram";
    else if (r.includes("facebook")) channel = "facebook";
    else if (r.includes("tiktok")) channel = "tiktok";
    else if (r.includes("google")) channel = "google_organic";
    else channel = "referral";
  }
  if (!channel && !ref) channel = "direct";
  const src = {
    utm_source: fromParam("utm_source"),
    utm_medium: fromParam("utm_medium"),
    utm_campaign: fromParam("utm_campaign"),
    utm_term: fromParam("utm_term"),
    utm_content: fromParam("utm_content"),
    referrer: ref,
    channel,
  };
  // remove undefined
  return Object.fromEntries(Object.entries(src).filter(([, v]) => v != null && v !== ""));
}

export default function Search() {
  const { t, lang, localizePath } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [leadSource, setLeadSource] = useState({});

  useEffect(() => {
    const captured = captureSource(searchParams);
    setLeadSource(captured);
    // persist for funnel attribution
    try { sessionStorage.setItem("sm_lead_source", JSON.stringify(captured)); } catch (e) { /* noop */ }
  }, [searchParams]);

  const [form, setForm] = useState({
    budget: 1200,
    num_participants: 4,
    group_type: "friends",
    accommodation: "whole_boat",
    boat_type: "any",
    destinations: ["", "", ""],
    date_mode: "flexible",
    start_date: "",
    end_date: "",
    flexible_month: lang === "it" ? "Agosto" : "August",
    flexible_duration: "1week",
    skipper_needed: "unsure",
    experiences: [],
    name: "",
    email: "",
    phone: "",
    phone_country_code: "+39",
    phone_country: "IT",
    privacy_consent: false,
    marketing_consent: false,
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const selectGroupType = (gt) => {
    const opt = groupOptions.find((g) => g.value === gt);
    update({ group_type: gt, num_participants: opt ? opt.suggested : form.num_participants });
  };
  const next = () => setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!form.privacy_consent) {
      toast.error(lang === "it" ? "È necessario il consenso privacy" : "Privacy consent is required");
      return;
    }
    if (!form.email || !form.name || !form.phone) {
      toast.error(lang === "it" ? "Nome, email e telefono richiesti" : "Name, email & phone required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        destinations: form.destinations.filter(Boolean),
        language: lang,
        lead_source: Object.keys(leadSource).length ? leadSource : null,
      };
      const res = await api.post("/search", payload);
      sessionStorage.setItem("sm_lead_id", res.data.lead_id);
      navigate(localizePath("/confirmation"));
    } catch (e) {
      toast.error(lang === "it" ? "Errore. Riprova." : "Error. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  const canNext = () => {
    if (step === 1) return form.budget > 0;
    if (step === 2) return form.num_participants > 0 && !!form.group_type;
    if (step === 3) return !!form.accommodation;
    if (step === 4) return !!form.boat_type;
    if (step === 5) return form.destinations.filter(Boolean).length >= 1;
    if (step === 6) {
      if (form.date_mode === "precise") return form.start_date && form.end_date;
      return true;
    }
    if (step === 7) return !!form.skipper_needed;
    return true;
  };

  return (
    <div data-testid="search-page" className="bg-white min-h-screen pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-5 lg:px-0">
        <div className="mb-8">
          <p className="label-eyebrow">{t("search.progress")} {step} {t("search.of")} {TOTAL_STEPS}</p>
          <div className="mt-3 flex gap-1.5">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span key={i} className={`step-dot ${i < step ? "active" : ""}`} />
            ))}
          </div>
        </div>

        {step === 1 && (
          <StepWrap title={t("search.s1Title")} help={t("search.s1Help")} icon={Wallet}>
            <div className="mt-8">
              <div className="flex items-baseline gap-3">
                <span className="font-serif text-5xl text-navy">€</span>
                <span data-testid="budget-display" className="font-serif text-6xl text-navy">
                  {form.budget.toLocaleString("it-IT")}
                </span>
              </div>
              <div className="mt-8">
                <Slider
                  data-testid="budget-slider"
                  min={500}
                  max={30000}
                  step={100}
                  value={[form.budget]}
                  onValueChange={(v) => update({ budget: v[0] })}
                  className="w-full"
                />
                <div className="mt-2 flex justify-between text-xs text-ink">
                  <span>€ 500</span>
                  <span>€ 30.000+</span>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {[1200, 2500, 5000, 10000, 20000].map((v) => (
                  <button
                    key={v}
                    data-testid={`budget-quick-${v}`}
                    onClick={() => update({ budget: v })}
                    className={`chip text-sm transition ${form.budget === v ? "bg-navy text-white border-navy" : "border-[#E2E8F0] text-navy hover:bg-sand"}`}
                  >
                    € {v.toLocaleString("it-IT")}
                  </button>
                ))}
              </div>

              <div className="mt-8 p-4 rounded-xl bg-sand/60 border border-[#E5E1D2]">
                <p className="text-[11px] uppercase tracking-wider text-gold font-semibold">{t("search.budgetCalloutEyebrow")}</p>
                <p className="mt-2 text-sm text-ink leading-relaxed">{t("search.budgetCalloutBody")}</p>
              </div>
            </div>
          </StepWrap>
        )}

        {step === 2 && (
          <StepWrap title={t("search.s2Title")} icon={Users}>
            <div className="mt-6">
              <p className="font-medium text-navy mb-3">{t("search.s2Sub")}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {groupOptions.map((g) => (
                  <OptionTile
                    key={g.value}
                    selected={form.group_type === g.value}
                    onClick={() => selectGroupType(g.value)}
                    testId={`group-${g.value}`}
                  >
                    <span className="font-medium text-navy">{g[lang]}</span>
                  </OptionTile>
                ))}
              </div>
              <div className="mt-10">
                <p className="text-sm text-ink">
                  {lang === "it" ? "Numero partecipanti (modificabile):" : "Number of participants (editable):"}
                </p>
                <div className="mt-3 flex items-center gap-4">
                  <button
                    data-testid="participants-minus"
                    onClick={() => update({ num_participants: Math.max(1, form.num_participants - 1) })}
                    className="h-12 w-12 rounded-full border border-navy/20 text-navy text-xl hover:bg-sand"
                  >−</button>
                  <span data-testid="participants-value" className="font-serif text-5xl text-navy w-20 text-center">{form.num_participants}</span>
                  <button
                    data-testid="participants-plus"
                    onClick={() => update({ num_participants: form.num_participants + 1 })}
                    className="h-12 w-12 rounded-full border border-navy/20 text-navy text-xl hover:bg-sand"
                  >+</button>
                </div>
              </div>
            </div>
          </StepWrap>
        )}

        {step === 3 && (
          <StepWrap title={t("search.s3Title")} icon={Ship}>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {accommOptions.map((a) => (
                <OptionTile
                  key={a.value}
                  selected={form.accommodation === a.value}
                  onClick={() => update({ accommodation: a.value })}
                  testId={`accom-${a.value}`}
                >
                  <span className="font-medium text-navy">{a[lang]}</span>
                </OptionTile>
              ))}
            </div>
          </StepWrap>
        )}

        {step === 4 && (
          <StepWrap title={t("search.s4Title")} icon={Sailboat}>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {boatOptions.map((b) => (
                <OptionTile
                  key={b.value}
                  selected={form.boat_type === b.value}
                  onClick={() => update({ boat_type: b.value })}
                  testId={`boat-${b.value}`}
                >
                  <div className="flex items-center gap-3">
                    <b.icon className="h-5 w-5 text-turquoise" />
                    <span className="font-medium text-navy">{b[lang]}</span>
                  </div>
                </OptionTile>
              ))}
            </div>
          </StepWrap>
        )}

        {step === 5 && (
          <StepWrap title={t("search.s5Title")} help={t("search.s5Sub")} icon={MapPin}>
            <div className="mt-6 space-y-4">
              {[0, 1, 2].map((i) => {
                const used = form.destinations.filter((d, idx) => d && idx !== i);
                return (
                  <div key={i}>
                    <Label className="text-xs uppercase tracking-wider text-ink">
                      {i === 0 ? (lang === "it" ? "Prima scelta" : "First choice") : i === 1 ? (lang === "it" ? "Seconda scelta" : "Second choice") : (lang === "it" ? "Terza scelta" : "Third choice")}
                    </Label>
                    <Select
                      value={form.destinations[i] || ""}
                      onValueChange={(v) => {
                        const arr = [...form.destinations];
                        arr[i] = v === "__none__" ? "" : v;
                        update({ destinations: arr });
                      }}
                    >
                      <SelectTrigger data-testid={`dest-${i}`} className="mt-1 h-12">
                        <SelectValue placeholder={i === 0 ? (lang === "it" ? "Seleziona destinazione" : "Select destination") : (lang === "it" ? "Opzionale" : "Optional")} />
                      </SelectTrigger>
                      <SelectContent>
                        {i !== 0 && <SelectItem value="__none__">{lang === "it" ? "— Nessuna —" : "— None —"}</SelectItem>}
                        {DEFAULT_DESTINATIONS.filter((d) => !used.includes(d)).map((d) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </StepWrap>
        )}

        {step === 6 && (
          <StepWrap title={t("search.s6Title")} icon={Calendar}>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              {[
                { v: "precise", it: "Ho date precise", en: "I have exact dates" },
                { v: "flexible", it: "Sono flessibile", en: "I'm flexible" },
                { v: "best_deals", it: "Le migliori occasioni", en: "Best deals only" },
              ].map((m) => (
                <OptionTile key={m.v} selected={form.date_mode === m.v} onClick={() => update({ date_mode: m.v })} testId={`datemode-${m.v}`}>
                  <span className="font-medium text-navy">{m[lang]}</span>
                </OptionTile>
              ))}
            </div>
            {form.date_mode === "precise" && (
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-ink">{lang === "it" ? "Inizio" : "Start"}</Label>
                  <Input type="date" data-testid="date-start" value={form.start_date} onChange={(e) => update({ start_date: e.target.value })} className="mt-1 h-12" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-ink">{lang === "it" ? "Fine" : "End"}</Label>
                  <Input type="date" data-testid="date-end" value={form.end_date} onChange={(e) => update({ end_date: e.target.value })} className="mt-1 h-12" />
                </div>
              </div>
            )}
            {form.date_mode === "flexible" && (
              <div className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-ink">{lang === "it" ? "Mese preferito" : "Preferred month"}</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {monthsIt.map((m) => (
                      <button
                        key={m}
                        data-testid={`month-${m}`}
                        onClick={() => update({ flexible_month: m })}
                        className={`chip text-xs ${form.flexible_month === m ? "bg-navy text-white border-navy" : "border-[#E2E8F0] text-navy hover:bg-sand"}`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-ink">{lang === "it" ? "Durata indicativa" : "Indicative duration"}</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {durations.map((d) => (
                      <button
                        key={d.value}
                        data-testid={`duration-${d.value}`}
                        onClick={() => update({ flexible_duration: d.value })}
                        className={`chip text-xs ${form.flexible_duration === d.value ? "bg-navy text-white border-navy" : "border-[#E2E8F0] text-navy hover:bg-sand"}`}
                      >
                        {d[lang]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </StepWrap>
        )}

        {step === 7 && (
          <StepWrap title={t("search.s7Title")} help={t("search.s7Help")} icon={Anchor}>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              {[
                { v: "yes", it: "Sì", en: "Yes" },
                { v: "no", it: "No", en: "No" },
                { v: "unsure", it: "Non lo so, consigliami tu", en: "Not sure, advise me" },
              ].map((m) => (
                <OptionTile key={m.v} selected={form.skipper_needed === m.v} onClick={() => update({ skipper_needed: m.v })} testId={`skipper-${m.v}`}>
                  <span className="font-medium text-navy">{m[lang]}</span>
                </OptionTile>
              ))}
            </div>
          </StepWrap>
        )}

        {step === 8 && (
          <StepWrap title={t("search.s8Title")} icon={Heart}>
            <div className="mt-6 flex flex-wrap gap-2">
              {experienceOptions.map((e) => {
                const selected = form.experiences.includes(e.value);
                return (
                  <button
                    key={e.value}
                    data-testid={`exp-${e.value}`}
                    onClick={() => {
                      const arr = selected ? form.experiences.filter((x) => x !== e.value) : [...form.experiences, e.value];
                      update({ experiences: arr });
                    }}
                    className={`chip text-sm transition ${selected ? "bg-navy text-white border-navy" : "border-[#E2E8F0] text-navy hover:bg-sand"}`}
                  >
                    {e[lang]}
                  </button>
                );
              })}
            </div>
          </StepWrap>
        )}

        {step === 9 && (
          <StepWrap title={t("search.s9Title")} help={t("search.s9Help")} icon={Mail}>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div>
                <Label>{lang === "it" ? "Nome" : "Name"}</Label>
                <Input data-testid="contact-name" value={form.name} onChange={(e) => update({ name: e.target.value })} className="mt-1 h-12" />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} className="mt-1 h-12" />
              </div>
              <div className="sm:col-span-2">
                <Label>{lang === "it" ? "Telefono / WhatsApp" : "Phone / WhatsApp"}</Label>
                <div className="mt-1 grid grid-cols-[140px,1fr] gap-2">
                  <select
                    data-testid="contact-phone-country"
                    value={form.phone_country_code}
                    onChange={(e) => {
                      const code = e.target.value;
                      const c = PHONE_COUNTRIES.find((x) => x.code === code);
                      update({ phone_country_code: code, phone_country: c?.iso || "" });
                    }}
                    className="h-12 rounded-md border border-input bg-background px-3 text-sm text-navy"
                  >
                    {PHONE_COUNTRIES.map((c) => (
                      <option key={c.iso} value={c.code}>
                        {c.flag} {c.code === "+" ? c.labels[lang] : `${c.code} ${c.labels[lang]}`}
                      </option>
                    ))}
                  </select>
                  <Input data-testid="contact-phone" value={form.phone} onChange={(e) => update({ phone: e.target.value })} className="h-12" placeholder={lang === "it" ? "Numero senza prefisso" : "Number without country code"} />
                </div>
                <p className="mt-1 text-[11px] text-ink/55">{t("search.countryCodeHelp")}</p>
              </div>
              <div className="sm:col-span-2 mt-2 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox data-testid="consent-privacy" checked={form.privacy_consent} onCheckedChange={(v) => update({ privacy_consent: !!v })} />
                  <span className="text-sm text-ink">{t("search.privacy")}</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox data-testid="consent-marketing" checked={form.marketing_consent} onCheckedChange={(v) => update({ marketing_consent: !!v })} />
                  <span className="text-sm text-ink">{t("search.marketing")}</span>
                </label>
              </div>
            </div>
          </StepWrap>
        )}

        <div className="mt-10 flex items-center justify-between">
          <Button
            data-testid="nav-back"
            variant="outline"
            onClick={back}
            disabled={step === 1}
            className="border-navy/20 text-navy hover:bg-sand h-11 rounded-md"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> {t("search.back")}
          </Button>
          {step < TOTAL_STEPS ? (
            <Button
              data-testid="nav-next"
              onClick={next}
              disabled={!canNext()}
              className="bg-navy text-white hover:bg-[#06101f] h-11 px-6 rounded-md"
            >
              {t("search.next")} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              data-testid="submit-search"
              onClick={submit}
              disabled={loading}
              className="bg-gold text-navy hover:bg-[#c89e29] h-11 px-6 rounded-md font-semibold"
            >
              {loading ? (lang === "it" ? "Invio…" : "Sending…") : t("search.submit")} <Sparkles className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepWrap({ title, help, icon: Icon, children }) {
  return (
    <div className="animate-fade-up">
      {Icon && (
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-sand text-navy">
          <Icon className="h-5 w-5" />
        </span>
      )}
      <h2 className="mt-5 font-serif text-3xl lg:text-4xl text-navy leading-tight tracking-tight">{title}</h2>
      {help && <p className="mt-2 text-sm text-ink max-w-xl">{help}</p>}
      {children}
    </div>
  );
}

function OptionTile({ selected, onClick, testId, children }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`text-left p-4 rounded-xl border transition-all min-h-[56px] ${
        selected ? "border-navy bg-navy/[0.03] ring-1 ring-navy" : "border-[#E2E8F0] hover:border-navy/40 hover:bg-sand"
      }`}
    >
      {children}
    </button>
  );
}
